'use client';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { type MouseEvent, type ReactNode, useState } from 'react';
import { Hits, useClearRefinements, useInstantSearch } from 'react-instantsearch';

import { Button } from '@commons/components/Button';
import ClearFilters from '@commons/components/Search/ClearFilters';
import NoResultsBoundary from '@commons/components/Search/NoResultsBoundary';
import SearchPagination from '@commons/components/Search/Pagination';
import Text from '@commons/components/Text';
import BaseContainer from '@commons/containers/BaseContainer';
import Flex from '@commons/containers/Flex';
import { useMediaQueries } from '@commons/contexts/responsive';

import styles from './Layout.module.scss';
import Result from './Result';

// Type definition for the SearchLayout component.

type Props = {
  topChildren?: ReactNode;
  sidebarChildren?: ReactNode;
  hasSidebar?: boolean;
};

/**
 * SearchLayout component.
 *
 * Renders a layout with a toggleable sidebar.
 *
 * @see https://www.algolia.com/doc/api-reference/widgets/hits/react/
 */

const SearchLayout = ({ topChildren, sidebarChildren, hasSidebar = false }: Props) => {
  const t = useTranslations('search');
  const { ready, minWidth } = useMediaQueries();

  const isMobile = ready && !minWidth.md;

  let defaultIsSidebarVisible = false;
  if (!isMobile) defaultIsSidebarVisible = hasSidebar;

  const [isSidebarVisible, setIsSidebarVisible] = useState(defaultIsSidebarVisible);

  const { results } = useInstantSearch();
  const { canRefine } = useClearRefinements();
  const hasResults = results?.nbHits > 0;
  const totalPages = results?.nbPages || 0;

  const buttonLabel = isSidebarVisible ? t('filters.hide') : t('filters.show');

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setIsSidebarVisible(!isSidebarVisible);
    event.currentTarget.blur();
  };

  return (
    <BaseContainer width={12}>
      <Flex
        gap="lg"
        width={12}
        align={{ lg: 'center' }}
        direction={{ xs: 'column', lg: 'row' }}
        marginBottom="xl"
      >
        {hasSidebar && (
          <Flex.Item width={{ xs: 12, lg: 4, xl: 3 }}>
            <Flex direction="row" align="center" justify="space-between">
              <Flex.Item>
                <Button
                  variant="tertiary"
                  icon="filter"
                  onClick={handleClick}
                  className={styles.filterToggle}
                >
                  {buttonLabel}
                </Button>
              </Flex.Item>
              {canRefine && (
                <Flex.Item>
                  <ClearFilters />
                </Flex.Item>
              )}
            </Flex>
          </Flex.Item>
        )}
        <Flex.Item width={hasSidebar ? { xs: 12, lg: 8, xl: 9 } : 12}>{topChildren}</Flex.Item>
      </Flex>
      <Flex
        gap={isSidebarVisible ? 'lg' : 'none'}
        width={12}
        direction={{ xs: 'column', lg: 'row' }}
      >
        {hasSidebar && (
          <Flex
            gap="md"
            width={{ xs: 12, lg: 4, xl: 3 }}
            direction="column"
            className={clsx(styles.sidebar, isSidebarVisible && styles.visible)}
          >
            {sidebarChildren}
          </Flex>
        )}
        <Flex.Item
          width={hasSidebar && isSidebarVisible ? { xs: 12, lg: 8, xl: 9 } : 12}
          className={styles.results}
        >
          <NoResultsBoundary fallback={<Text level="body-lg">{t('noResults')}</Text>}>
            <Hits
              hitComponent={Result}
              classNames={{
                root: styles.root,
                list: styles.list,
                item: styles.result,
              }}
            />
          </NoResultsBoundary>
          {hasResults && totalPages > 1 && <SearchPagination />}
        </Flex.Item>
      </Flex>
    </BaseContainer>
  );
};

export default SearchLayout;
